import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import NotificationToggles from './NotificationToggles';

function UnsubscribePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [preferences, setPreferences] = useState({
    newBulletin: true,
    newForumPost: false,
    newTicket: false,
    newForumComment: true,
    forumMention: true
  });

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/api/auth/unsubscribe/${token}`);
        if (response.data.success) {
          setPreferences(response.data.notificationPreferences);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired unsubscribe link');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.put(`${getApiBaseUrl()}/api/auth/unsubscribe/${token}`, {
        notificationPreferences: preferences
      });

      if (response.data.success) {
        setPreferences(response.data.notificationPreferences);
        setSuccess('Your email preferences have been updated');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.put(`${getApiBaseUrl()}/api/auth/unsubscribe/${token}`, {
        unsubscribeAll: true
      });

      if (response.data.success) {
        setPreferences(response.data.notificationPreferences);
        setSuccess('You have been unsubscribed from all email notifications');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unsubscribe');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error && !preferences) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl border border-border p-8 text-center">
          <div className="text-destructive mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Invalid Link</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-card rounded-lg shadow-xl border border-border p-8">
          <div className="text-center mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-primary mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h1 className="text-3xl font-bold text-foreground">Email Preferences</h1>
            <p className="text-muted-foreground mt-2">
              Manage which email notifications you receive
            </p>
          </div>

          <div className="mb-6">
            <NotificationToggles
              preferences={preferences}
              onChange={setPreferences}
              disabled={saving}
              variant="bordered"
            />
          </div>

          {error && (
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>

            <button
              onClick={handleUnsubscribeAll}
              disabled={saving}
              className="w-full border border-destructive text-destructive py-3 rounded-lg font-semibold hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Unsubscribe from All
            </button>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            You can also manage these settings from your{' '}
            <Link to="/account" className="text-primary hover:underline">
              account page
            </Link>{' '}
            when logged in.
          </p>
        </div>
      </div>
    </div>
  );
}

export default UnsubscribePage;
